/**
 * Steal from Masters — plan generation (docs/design/STEAL-SPEC.md "Plan
 * generation"). A second, deterministic plan generator feeding the SAME
 * review queue ShiftBar's AI-backed /api/shift path already produces steps
 * for — see src/services/shift-service.ts's ShiftPlanStep and
 * src/components/Panels/ReviewQueue.tsx. No new application machinery.
 */
import type { Chord, MusicalKey, Mode } from '@/types/chord';
import type { Influence } from '@/types/influence';
import type { ShiftPlanStep } from '@/services/shift-service';
import { MOVES, type MoveContext } from '@/lib/moves';
import { STEAL_MOVE_IDS } from './extract';

const WEIGHT_FLOOR = 0.15;
const MAX_STEPS = 4;

export interface StealApplyContext {
  chords: Chord[];
  key: MusicalKey;
  mode: Mode;
}

/**
 * Rank the influence's traits by weight (dropping anything below the floor),
 * then walk the progression once per trait collecting the move's own
 * applicable() hits, taking the earliest still-unused chord index per trait.
 * "Unused" enforces the spread rule: every chord index is claimed by at most
 * one step, so nothing ever stacks two moves, let alone three.
 *
 * Deterministic by construction: Array.sort is stable (ES2019+), traits are
 * always built in STEAL_MOVE_IDS order (see extract.ts's learnPiece), so
 * weight ties resolve to that same fixed order every time; candidate
 * selection always walks the chord array from index 0. Same influence + same
 * progression → same plan, no randomness anywhere.
 */
export function planFromInfluence(influence: Influence, ctx: StealApplyContext): ShiftPlanStep[] {
  const sorted = [...ctx.chords].sort((a, b) => a.startBeat - b.startBeat);

  const rankedTraits = influence.traits
    .filter((t) => t.weight >= WEIGHT_FLOOR)
    .slice()
    .sort((a, b) => b.weight - a.weight);

  const usedIndexes = new Set<number>();
  const steps: ShiftPlanStep[] = [];

  for (const trait of rankedTraits) {
    if (steps.length >= MAX_STEPS) break;

    const move = MOVES.find((m) => m.id === trait.moveId);
    if (!move) continue; // trait references a move id outside the current registry — skip, don't throw

    for (let index = 0; index < sorted.length; index++) {
      if (usedIndexes.has(index)) continue;

      const moveCtx: MoveContext = { chords: sorted, index, key: ctx.key, mode: ctx.mode };
      if (!move.applicable(moveCtx)) continue;

      usedIndexes.add(index);
      steps.push({
        moveId: trait.moveId,
        index,
        rationale: `${influence.name} ${trait.evidence} — here too, maybe.`,
      });
      break; // one step per trait per pass
    }
  }

  return steps;
}

/** Exported for tests that want to assert against the same canonical order the ranking tiebreak relies on. */
export { STEAL_MOVE_IDS };
